import type { SignalSource } from '../../../../shared/enums';
import type { UPLOAD_CITIES } from '../../../../shared/fields';
import { shell } from '../../app';
import { timeAgoShort } from '../../logic/dates';
import type { SourceRow, SourceRows } from '../../logic/sourceHealth';
import { btn, empty, row, section } from '../../ui/components';
import { modal } from '../../ui/dialog';
import type { Modal } from '../../ui/dialog';
import { h, replaceChildren } from '../../ui/dom';
import type { Child } from '../../ui/dom';
import type { View } from '../../ui/router';
import { cityUpload } from './cityUpload';
import { createInbox } from './inbox';
import { planCheckHowTo } from './planCheckHowTo';

/** The sources sheet (D74): what still needs the rep first, with the exact instruction and one action
 *  each; what reads itself, with its cadence, its last success and its trouble; what has never brought a
 *  business, folded. Shared by the phone's own page and the desk's right pane. */

type UploadCity = (typeof UPLOAD_CITIES)[number];

export interface SheetActions {
  refresh: () => void;
  importFile: (city: UploadCity) => void;
  setup: () => void;
  /** "Run now" on a server row (D77): the desk gives it; the phone has Refresh alone. */
  runNow?: (source: SignalSource | 'places_retention') => void;
}

const link = (label: string, url: string): HTMLElement =>
  h('a', { class: 'btn btn--subtle btn--sm', href: url, target: '_blank', rel: 'noopener' }, label);

const trailingOf = (r: SourceRow, on: SheetActions): Child => {
  if (r.action === 'import')
    return btn('Import file', {
      variant: 'subtle',
      size: 'sm',
      onClick: () => on.importFile(r.source as UploadCity),
    });
  if (r.action && r.url) return link(r.action === 'open' ? 'Open page' : 'Open search', r.url);
  const source = r.source;
  if (source !== 'roster_geocode' && on.runNow && r.mode === 'server' && r.state !== 'off')
    return btn('Run now', { variant: 'subtle', size: 'sm', onClick: () => on.runNow?.(source) });
  return null;
};

const toneOf = (r: SourceRow): 'danger' | 'warn' | 'none' =>
  r.state === 'red' || r.state === 'blocked'
    ? 'danger'
    : r.state === 'failing' || r.state === 'stale' || r.state === 'waiting'
      ? 'warn'
      : 'none';

const sourceRow = (r: SourceRow, on: SheetActions): HTMLElement =>
  row({
    title: r.name,
    meta: [r.text, r.hint].filter(Boolean).join(' — '),
    tone: toneOf(r),
    trailing: trailingOf(r, on),
  });

/** The enrichment's Google lookups this month against its ceiling (D79). */
export interface GoogleUsage {
  lookups_month: number;
  cap: number;
}
/** Amber from here: the month is nearly spent. */
const NEARLY_SPENT = 0.9;

const usageText = (g: GoogleUsage): string =>
  `${g.lookups_month} of ${g.cap} Google lookups this month${g.lookups_month >= g.cap ? ' · the rest wait for next month' : ''}`;

export const sourcesSheet = (
  on: SheetActions,
): {
  el: HTMLElement;
  update: (rows: SourceRows, updatedAt: string | null, google?: GoogleUsage) => void;
  /** Instead of the rows: why there are none yet (loading, or the list could not come). */
  show: (note: HTMLElement) => void;
} => {
  const body = h('div', { class: 'sheet__body' });
  const updated = h('span', { class: 'sheet__updated' });
  const usage = h('span', { class: 'sheet__google' });
  const el = h(
    'div',
    { class: 'sources' },
    body,
    h(
      'div',
      { class: 'sheet__foot' },
      btn('Set up the bookmark', { variant: 'subtle', size: 'sm', onClick: on.setup }),
      btn('Refresh', { variant: 'subtle', size: 'sm', onClick: on.refresh }),
      updated,
      usage,
    ),
  );
  const update = (rows: SourceRows, updatedAt: string | null, google?: GoogleUsage) => {
    const server = rows.current.filter((r) => r.mode === 'server');
    const manual = rows.current.filter((r) => r.mode !== 'server');
    replaceChildren(
      body,
      rows.needsYou.length
        ? section(
            { title: 'Needs you', count: rows.needsYou.length, tone: 'warn' },
            rows.needsYou.map((r) => sourceRow(r, on)),
          )
        : section({ title: 'Needs you' }, h('p', { class: 'card__summary' }, 'Nothing waiting on you.')),
      section(
        { title: 'Automatic', count: server.length },
        server.map((r) => sourceRow(r, on)),
      ),
      manual.length
        ? section(
            { title: 'Up to date', count: manual.length },
            manual.map((r) => sourceRow(r, on)),
          )
        : null,
      rows.nothingYet.length
        ? h(
            'details',
            { class: 'sig-sources__all' },
            h('summary', {}, `Nothing yet · ${rows.nothingYet.length}`),
            rows.nothingYet.map((r) => sourceRow(r, on)),
          )
        : null,
    );
    updated.textContent = updatedAt ? `Updated ${timeAgoShort(updatedAt)}` : '';
    usage.textContent = google ? usageText(google) : '';
    usage.classList.toggle('text-warn', !!google && google.lookups_month >= google.cap * NEARLY_SPENT);
  };
  return { el, update, show: (note) => replaceChildren(body, note) };
};

/** The bookmark setup, in a dialog titled as its heading (D66). */
export const setupDialog = (): Modal => {
  const steps = planCheckHowTo();
  steps.open = true;
  return modal(steps.querySelector('summary')?.textContent ?? 'Send to CRM', steps);
};

/** The sheet's dialogs: the bookmark setup, and the city upload preselected on the row's city. */
export const sheetDialogs = (
  onImported: () => void,
): { actions: Omit<SheetActions, 'refresh'>; remove: () => void } => {
  const setup = setupDialog();
  let importDialog: Modal | null = null;
  return {
    actions: {
      setup: setup.open,
      importFile: (city) => {
        importDialog?.remove();
        const box = cityUpload(onImported, city);
        box.open = true;
        importDialog = modal('Import a city file', box);
        importDialog.open();
      },
    },
    remove: () => {
      setup.remove();
      importDialog?.remove();
    },
  };
};

/** The phone's own sources page (D74): the sheet on its screen, from the stored copy, refreshed on demand. */
export const sourcesSheetView: View = ({ root }) => {
  shell.setChrome(true);
  shell.setTitle('Sources', { back: '#/signals' });
  const inbox = createInbox(() => paint());
  const dialogs = sheetDialogs(() => void inbox.load());
  const sheet = sourcesSheet({ refresh: () => void inbox.load(), ...dialogs.actions });
  root.appendChild(h('div', { class: 'page page--sources' }, sheet.el));
  const paint = () => {
    const { sources, loaded, error } = inbox.state;
    if (sources) return sheet.update(sources.rows, inbox.state.updatedAt, sources.google);
    // nothing ever loaded on this device: say why the sheet is empty rather than paint nothing
    sheet.show(loaded && error ? empty(error.text, error.hint) : empty('Loading…'));
  };
  paint();
  void inbox.load();
  return () => {
    inbox.dispose();
    dialogs.remove();
  };
};
